import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

function initHero(): void {
    const hero = document.querySelector<HTMLElement>('.our-crafts-page [data-craft-hero]');
    if (!hero) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const code = hero.querySelector<HTMLElement>('[data-hero-code]');
    const dashboard = hero.querySelector<HTMLElement>('[data-hero-dashboard]');
    const revealItems = Array.from(hero.querySelectorAll<HTMLElement>('[data-hero-reveal]'));

    if (!prefersReducedMotion) {
        gsap.set(revealItems, { autoAlpha: 0, y: 24 });
        if (code) {
            gsap.set(code, { autoAlpha: 0, x: -72, y: 28, rotateY: 24, rotateX: 7 });
        }

        if (dashboard) {
            gsap.set(dashboard, { autoAlpha: 0, x: 72, y: 32, rotateY: -24, rotateX: 7 });
        }

        let settled = false;
        const settle = (): void => {
            if (settled) return;

            settled = true;
            gsap.set(revealItems, { autoAlpha: 1, y: 0, clearProps: 'visibility' });
        };

        const timeline = gsap.timeline({ defaults: { ease: 'power3.out' }, onComplete: settle });

        if (code) {
            timeline.to(code, { autoAlpha: 1, x: 0, y: 0, rotateY: 0, rotateX: 0, duration: 1.05 }, 0.02);
        }

        if (dashboard) {
            timeline.to(dashboard, { autoAlpha: 1, x: 0, y: 0, rotateY: 0, rotateX: 0, duration: 1.05 }, 0.12);
        }

        if (revealItems.length > 0) {
            timeline.to(revealItems, { autoAlpha: 1, y: 0, duration: 0.72, stagger: 0.08 }, 0.28);
        }

        window.setTimeout(settle, 1800);
    }

    if (prefersReducedMotion || (!code && !dashboard)) return;

    hero.addEventListener('mousemove', event => {
        const rect = hero.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width - 0.5;
        const y = (event.clientY - rect.top) / rect.height - 0.5;

        if (code) {
            gsap.to(code, {
                x: x * 18,
                y: y * 14,
                rotateX: -y * 4,
                rotateY: 14 + x * 7,
                duration: 0.5,
                ease: 'power2.out',
                overwrite: 'auto'
            });
        }

        if (dashboard) {
            gsap.to(dashboard, {
                x: x * -18,
                y: y * 14,
                rotateX: -y * 4,
                rotateY: -14 + x * 7,
                duration: 0.5,
                ease: 'power2.out',
                overwrite: 'auto'
            });
        }
    });

    hero.addEventListener('mouseleave', () => {
        if (code) {
            gsap.to(code, { x: 0, y: 0, rotateX: 0, rotateY: 14, duration: 0.8, ease: 'power3.out', overwrite: 'auto' });
        }

        if (dashboard) {
            gsap.to(dashboard, { x: 0, y: 0, rotateX: 0, rotateY: -14, duration: 0.8, ease: 'power3.out', overwrite: 'auto' });
        }
    });
}

function initCompanyStory(): void {
    const story = document.querySelector<HTMLElement>('.our-crafts-page [data-company-story]');
    const steps = Array.from(story?.querySelectorAll<HTMLElement>('[data-company-step]') ?? []);
    if (!story || steps.length === 0) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const progress = story.querySelector<HTMLElement>('[data-company-progress]');
    const stageIndex = story.querySelector<HTMLElement>('[data-company-stage-index]');
    const stageLogo = story.querySelector<HTMLImageElement>('[data-company-stage-logo]');
    const stageKicker = story.querySelector<HTMLElement>('[data-company-stage-kicker]');
    const stageName = story.querySelector<HTMLElement>('[data-company-stage-name]');
    const stageSummary = story.querySelector<HTMLElement>('[data-company-stage-summary]');
    let activeIndex = -1;

    const setActive = (index: number): void => {
        const nextIndex = Math.max(0, Math.min(index, steps.length - 1));
        const nextStep = steps[nextIndex];
        if (!nextStep) return;

        activeIndex = nextIndex;
        story.style.setProperty('--company-accent', nextStep.dataset.companyAccent ?? '#ed2939');
        story.style.setProperty('--company-accent-rgb', nextStep.dataset.companyAccentRgb ?? '237, 41, 57');

        steps.forEach((step, stepIndex) => step.classList.toggle('is-active', stepIndex === nextIndex));

        if (stageIndex) stageIndex.textContent = nextStep.dataset.companyLabel ?? `0${nextIndex + 1}`;
        if (stageKicker) stageKicker.textContent = nextStep.dataset.companyKicker ?? '';
        if (stageName) stageName.textContent = nextStep.dataset.companyName ?? '';
        if (stageSummary) stageSummary.textContent = nextStep.dataset.companySummary ?? '';
        if (stageLogo && nextStep.dataset.companyLogo) {
            stageLogo.src = nextStep.dataset.companyLogo;
        }

        if (!prefersReducedMotion) {
            const stageItems = [stageLogo, stageKicker, stageName, stageSummary].filter((item): item is HTMLElement => Boolean(item));
            gsap.fromTo(
                stageItems,
                { autoAlpha: 0, y: 14 },
                { autoAlpha: 1, y: 0, duration: 0.34, stagger: 0.035, ease: 'power2.out', overwrite: 'auto' }
            );
        }
    };

    if (prefersReducedMotion) {
        setActive(0);
        steps.forEach(step => step.classList.add('is-active'));
        if (progress) progress.style.transform = 'scaleY(1)';
        return;
    }

    setActive(0);

    steps.forEach((step, index) => {
        ScrollTrigger.create({
            trigger: step,
            start: 'top 58%',
            end: 'bottom 42%',
            onEnter: () => setActive(index),
            onEnterBack: () => setActive(index)
        });
    });

    if (progress) {
        ScrollTrigger.create({
            trigger: story,
            start: 'top top',
            end: 'bottom bottom',
            scrub: true,
            onUpdate: self => {
                progress.style.transform = `scaleY(${Math.max(0.04, self.progress)})`;
                const index = Math.min(steps.length - 1, Math.round(self.progress * (steps.length - 1)));
                if (index !== activeIndex) {
                    setActive(index);
                }
            }
        });
    }

    window.addEventListener('resize', () => ScrollTrigger.refresh(), { passive: true });
}

export function initOurCraftsPage(): void {
    if (!document.querySelector('.our-crafts-page')) return;

    initHero();
    initCompanyStory();
}
